import type { ProjectCopyMap } from './types';

export const zhTW: ProjectCopyMap = {
  kura: {
    tagline: '個人 Agent 作業系統 —— 一個本機常駐程式，多個輕量用戶端。',
    summary:
      'Rust 控制平面在本機執行，掌管執行時、LLM 供應方、通道連接器、儲存與事件。用戶端保持輕量：React Web UI、全螢幕 Rust TUI、聊天通道連接器與 TypeScript SDK —— 全部基於同一套 JSON Schema 契約。',
    body: [
      '常駐程式才是產品本體。所有必須保證正確的部分 —— 工作階段狀態、供應方路由、工具 harness、事件紀錄 —— 都收斂在一個 Rust workspace 裡，透過本機 HTTP API 對外暴露；用戶端不過是它的一個檢視。常駐程式執行檔為 `dope-cli`，HTTP API 為 `dope-api`。',
      '跨語言契約以 JSON Schema 形式存放在 `schemas/`，是 API、事件與設定的唯一事實來源 —— Rust 常駐程式與 TypeScript 用戶端基於同一份定義產生，而不是靠慣例手動對齊。',
      '專案明確的工作假設是：長生命週期的 Agent 狀態必須可觀測、可重播、可安全演進；脈絡工程、記憶、規劃、交接與策略是重新設計，而非小幅修補。',
    ],
    highlights: [
      'Rust 控制平面：執行時、LLM 供應方、通道連接器、儲存、事件、HTTP API 與 harness 收斂在一個 workspace。',
      '同一常駐程式之上的三種介面：React 19 + Vite Web UI、全螢幕 Rust TUI（`dope-tui`）、聊天通道連接器。',
      'TypeScript 用戶端 SDK（`@dope/client`），基於共用的 JSON Schema 契約產生。',
      '測試與正式環境隔離 —— `~/.dope-test` 使用 19192 連接埠，`~/.dope` 使用 19191；測試環境預設關閉真實連接器。',
      '早期的 Go 常駐程式已被 Rust workspace 完全取代，遷移過程記錄在 `crates/MIGRATION.md`。',
    ],
  },

  gozen: {
    tagline: '多 CLI 環境切換器，內建 API 代理自動故障移轉。',
    summary:
      '在同一處切換 Claude Code、Codex、OpenCode 的執行環境；上游供應商不穩時也不中斷 —— GoZen 代理 API 流量並自動切換到可用線路。',
    body: [
      '所有 API 設定集中在 `~/.zen/zen.json`，單一 `zend` 常駐程式同時承載代理服務與具密碼保護的 Web UI。目錄可綁定到指定 profile 與 CLI，在專案目錄直接輸入 `zen` 就會以正確的用戶端連上正確的供應商，不需附加參數。',
      '真正的價值在代理層：請求依 thinking、image、longContext 等特徵路由，主線路無法使用時自動切到備用供應商。v3 新增了依供應商 / 模型 / 專案維度的 token 與成本統計、具警告/降級/阻擋動作的預算控制、供應商健康檢查、多種負載平衡策略以及 Webhook 通知。',
      '設定可透過 WebDAV、S3、GitHub Gist 或 GitHub 儲存庫在多裝置間同步，並以 AES-256-GCM 加密。Web UI 採用工作階段驗證，權杖傳輸使用 RSA 加密。',
    ],
    highlights: [
      '一個切換器涵蓋 Claude Code、Codex、OpenCode，可透過目錄綁定依專案設定。',
      '內建 HTTP 代理，主線路故障時自動切換備用供應商，並支援情境化路由（thinking、image、長脈絡）。',
      '依供應商、模型、專案統計用量與成本，支援日 / 週 / 月預算上限。',
      '供應商健康監控（延遲與錯誤率），支援故障移轉、輪詢、最低延遲、最低成本等平衡策略。',
      '透過 WebDAV、S3、GitHub Gist 或 GitHub 儲存庫同步設定，並以 AES-256-GCM 加密。',
      'Bot 閘道：可從 Telegram、Discord、Slack、Lark 或 Messenger 監控與控制 Claude Code 工作階段。',
      '`zen upgrade` 自我更新，並提供 zsh / bash / fish 補完。',
    ],
    commandLabels: ['安裝', '首次執行'],
  },

  loopforge: {
    tagline: '獨立的本地遊戲開發 Agent，配桌面工作台。',
    summary:
      'Loopforge 直接在一般的遊戲儲存庫裡工作：把想法變成可玩的實驗，蒐集技術與人工兩類證據，協助你明確做出保留、捨棄或重構的決定。Agent 是控制平面，桌面 Workbench 是使用介面；CLI 與 Skills 是內部能力，不需要你手動驅動。',
    body: [
      '打開專案就是全部的準備工作：Workbench 透過系統資料夾選擇器接入一個遊戲儲存庫，接著自動啟動或重新連線該專案的 Agent，並載入受限的專案脈絡 —— 過程中不暴露供應商憑證、環境變數或存取權杖。',
      '介面是圍繞專案而非圍繞 Agent 行程組織的。專案選單切換儲存庫與專案層級檢視，標題區承載專案識別與必要操作，主工作區放置各模式專屬工具，浮動模式工具列在探索、設計、建置、測試之間切換。聊天位於工作區旁而非取而代之，因為專案狀態與證據都寫在遊戲儲存庫裡 —— 聊天紀錄不是唯一的事實來源。',
      '邊界是刻意劃分的：`apps/agent` 承載領域 Agent 與所有使用者可見行為；`cli` 承載確定性的專案操作與無頭轉接器，明確不是控制平面；`skills` 承載需要情境判斷的版本化能力；`contracts` 承載版本化的結構定義。Kura 提供通用的模型、工作階段與執行時能力，不含任何 Loopforge 狀態；發布版應用內嵌 Agent 與鎖定版本的 Kura sidecar。',
    ],
    highlights: [
      '桌面 Workbench（Tauri + React）是產品本體，只負責 Agent sidecar 的生命週期。',
      '專案直接從一般遊戲儲存庫開啟；載入的專案脈絡不含憑證與環境變數。',
      '模式工具列涵蓋探索、設計、建置、測試，Agent 聊天在工作區旁而非取代工作區。',
      '狀態與證據寫入儲存庫，「發生過什麼」不依賴聊天紀錄。',
      '確定性操作與版本化 Skills 仍可用於自動化與診斷 —— 它們是內部能力，不是入口。',
      '通用的模型、工作階段與執行時來自鎖定版本的 Kura sidecar，領域邏輯留在 Loopforge 這一側。',
    ],
    commandLabels: ['從原始碼執行 Workbench'],
    requirements:
      'Git、Node.js 22 與 pnpm、Rust 與 Cargo、Python 3.11+ 與 uv；僅在使用 Godot 流程時需要 Godot 4。目前沒有打包發布版，桌面應用需從原始碼建置。',
  },

  pingo: {
    tagline: '從零設計的 Web Canvas 算繪引擎。',
    summary:
      '高效能 TSX 執行時與具確定性的 Rust/WASM Core，原生虛擬捲動與 Canvas 原生文字編輯，以及具備 SCSS/Less 建置期管線的版本化 CSS 子集。內建對齊 shadcn 的 UI 元件庫（約 50 個元件），官網元件文件全部由引擎自身在 Canvas 上即時算繪。',
    body: [
      'Pingo 已走過里程碑路線圖的早期階段：編輯、事件、命中測試與無障礙主鏈已經落地，引擎現在提供真正可用的原語 —— 文字、影像、圖形等基礎元素，原生虛擬捲動，以及含輸入法支援的 Canvas 原生文字編輯。技術決策仍以 `docs/design.md` 為準。',
      '樣式系統是層疊語意可預期的版本化 CSS 子集，並附帶 SCSS/Less 建置期管線，既有樣式表可平滑遷移。其上為對齊 shadcn 的 UI 元件庫，約 50 個元件 —— 按鈕、表單、浮層、資料展示 —— 全部算繪於 Canvas 而非 DOM。',
      '官網本身就是佐證：每個元件文件頁都嵌入由引擎自身即時算繪的 demo，可在瀏覽器中直接互動。文件中看得到的，就是正在 Pingo 上執行的。',
    ],
    highlights: [
      '高效能 TSX 執行時，虛擬捲動是一等原語，而非上層附加實作。',
      '具確定性的 Rust/WASM Core，透過版本化二進位 ABI 對外暴露。',
      'Canvas 原生文字編輯：EditContext 優先、集中式 textarea proxy 降級，並支援可確定性的 IME 錄製與重播。',
      '平台資格認證與里程碑完成明確脫鉤 —— 未認證的平台不做效能宣稱。',
    ],
    commandLabels: ['從儲存庫原始碼執行平台探針'],
    requirements: 'Node.js 22.12+、pnpm 10.33.2、Rust 1.96.0，並安裝 wasm32-unknown-unknown target。',
  },

  deckle: {
    tagline: '面向大量 AI 產生 Web 產物的無限畫布執行時。',
    summary:
      '數百個活躍 iframe 無法規模化，而把頁面全部壓平成圖片又會失去選取與事件定位。Deckle 以「保留式產物」建模 —— 原始碼、持久狀態、互動樹、繪製快取與可選的活躍執行時 —— 讓 Figma 式的選取與啟用得以保留。與後端無關的引擎契約已實作並有測試覆蓋；瀏覽器證據門檻尚未通過。',
    body: [
      '每個 iframe 都保留著瀏覽脈絡、DOM/CSS 狀態、指令碼 realm、資源與算繪狀態，因此讓數百個產生頁面同時存活的代價極高。設計上的回答是「保留式產物」模型 —— `產物 = 原始碼 + 持久狀態 + 互動樹 + 繪製快取 + 可選的活躍執行時` —— 其中快照僅僅是繪製快取，文件與互動模型始終可用，從而支援選取、事件路由、啟用與版本安全的還原。',
      '由於產物來自 Agent，它們是逐步抵達的，因此串流並非包在節點模型外的一層，而是模型本身的一等屬性。每種內容都在自己的邊界處提交 —— 一個字素、一行、一個閉合的 Markdown 結構、一個 JSON 值、一個已確定的 HTML 標籤 —— 且該邊界只前進不後退，讀者永遠不會看到某個解讀被收回。',
      '目前已實作並有測試的部分：場景交易、相機與空間虛擬化、生命週期與預算、產物版本、內容淨化、受控執行時協定、保留式算繪與內部命中測試。尚未完成的部分：M0 的瀏覽器證據門檻未通過，對實驗性 HTML-in-Canvas API 的支援是探針偵測到的「能力」而非承諾，絕對效能與記憶體門檻在有實測數據前保持未設定。目前尚無任何介面構成穩定的公開契約。',
      '這個名字本身就是論點。deckle 是手工造紙時框住紙漿的定型框，紙漿尚未沉定，它留下的毛邊就叫 deckle edge —— 為尚未到齊的內容框定邊界，並讓這條邊界可見，而不是假裝這張紙已經完成。',
    ],
    highlights: [
      '保留式產物模型：快照只是繪製快取，因此選取與事件定位不會遺失。',
      '串流內建於節點模型，各類內容各有只進不退的提交邊界 —— 不存在被收回的解讀。',
      '引擎契約已實作並有測試：場景交易、相機與空間虛擬化、生命週期、預算、版本、淨化、執行時協定、保留式算繪、命中測試。',
      '安全是一等公民套件 —— sanitizer、URL 策略、配額與能力控制 —— 而不是後期加固。',
      '採用 Apache-2.0，看重的是專利授權：串流邊界模型與 Canvas 原生算繪剖面屬於需要明確授權的實作性工作。',
      '函式庫在 0.3.0 之前以 `@dopejs/canvas-*` 發布，自 0.4.0 起更名為 `@dopejs/deckle-*`，僅更名，程式碼不變。',
    ],
    requirements: 'Node.js 22.12+ 與 pnpm 10.33.2。',
  },
};
